import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Stack,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";

type Rating = number; // 0..5

interface VideoRatingModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (ratings: { question: string; rating: Rating }[]) => void;
}

const QUESTIONS = [
  "The information in this video was of interest to me",
  "The information in this video is likely to improve my clinical practice or research",
  "I would recommend this video to my colleagues",
];

const StarRating: React.FC<{
  value: Rating;
  onChange: (v: Rating) => void;
  size?: number;
  label?: string;
}> = ({ value, onChange, size = 28 }) => {
  const [hover, setHover] = useState<number>(0);

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = hover ? n <= hover : n <= value;
        return (
          <IconButton
            key={n}
            size="small"
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange(n)}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            sx={{
              padding: 0.5,
              "&:hover": { backgroundColor: "transparent" },
              color: filled ? "warning.main" : "text.disabled",
            }}
          >
            {filled ? (
              <StarIcon sx={{ fontSize: size }} />
            ) : (
              <StarBorderIcon sx={{ fontSize: size }} />
            )}
          </IconButton>
        );
      })}
    </Box>
  );
};

const VideoRatingModal: React.FC<VideoRatingModalProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const [ratings, setRatings] = useState<Rating[]>([0, 0, 0]);

  const handleChange = (idx: number, val: Rating) => {
    setRatings((r) => {
      const copy = [...r];
      copy[idx] = val;
      return copy;
    });
  };

  const handleSubmit = () => {
    const payload = QUESTIONS.map((q, i) => ({
      question: q,
      rating: ratings[i],
    }));
    onSubmit(payload);
  };

  const allZero = ratings.every((r) => r === 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="video-rating-title"
    >
      <DialogTitle
        id="video-rating-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pl: 2,
          pr: 1,
        }}
      >
        <Typography variant="h6">Please rate this video</Typography>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, pb: 3 }}>
        <Stack spacing={4}>
          {QUESTIONS.map((q, i) => (
            <Box key={i}>
              <Typography variant="body1" gutterBottom>
                {q}
              </Typography>
              <StarRating
                value={ratings[i]}
                onChange={(v) => handleChange(i, v)}
              />
            </Box>
          ))}
        </Stack>

        <Box mt={4} textAlign="center">
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={allZero}
            sx={{ minWidth: 140, px: 4 }}
          >
            Rate video
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VideoRatingModal;
